import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { aiVetAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatMessage from '../components/ChatMessage';
import {
  PaperAirplaneIcon,
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AIVetChat = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const { data: session, isLoading } = useQuery(
    ['aiVetSession', sessionId],
    () => aiVetAPI.getSession(sessionId),
    { enabled: !!sessionId }
  );

  const sendMessageMutation = useMutation(
    ({ sessionId, messageData }) => aiVetAPI.sendMessage(sessionId, messageData),
    {
      onMutate: () => {
        setIsTyping(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['aiVetSession', sessionId]);
        setMessage('');
        setIsTyping(false);
      },
      onError: () => {
        setIsTyping(false);
      }
    }
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [session?.messages, isTyping]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!message.trim() || isTyping) return;

    sendMessageMutation.mutate({
      sessionId: parseInt(sessionId),
      messageData: { content: message.trim() }
    });
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading chat..." />;
  }

  if (!session) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Chat session not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The chat session you're looking for doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <Link to="/virtual-vet" className="btn btn-primary">
              Start New Chat
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const sessionData = session.data;
  const messages = sessionData.messages || [];

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/virtual-vet')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {sessionData.title || `Chat about ${sessionData.pet_name}`}
              </h1>
              <p className="text-sm text-gray-600">
                AI Veterinary Assistant • {sessionData.pet_name} ({sessionData.pet_type})
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
              AI Assistant - Not a substitute for professional care
            </div>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start the conversation by asking about {sessionData.pet_name}'s health or behavior.
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                isUser={msg.role === 'user'}
              />
            ))}
            {isTyping && (
              <div className="flex justify-start mb-4">
                <div className="flex">
                  <div className="mr-2">
                    <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="w-5 h-5 text-secondary-600" />
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-gray-100 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 p-4">
        <form onSubmit={handleSendMessage} className="flex space-x-3">
          <div className="flex-1">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Ask about ${sessionData.pet_name}'s health, behavior, or care...`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows="2"
              disabled={isTyping}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
          </div>
          <button
            type="submit"
            disabled={!message.trim() || isTyping}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </form>
        <div className="mt-2 text-xs text-gray-500 text-center">
          AI responses are for informational purposes only. Always consult a licensed veterinarian for medical advice.
        </div>
      </div>
    </div>
  );
};

export default AIVetChat;